import {IMAGE_PLACEHOLDER_FULL_ID} from '@packages/systems/plugins-utils/Asset';
import {parseTokenJson} from '@packages/systems/core/utils/EmbedUtils/shared';
import type {OgImageValue} from './types';
import {singleMemo} from '@packages/utilities/memo';

const PLACEHOLDER_OG_IMAGE: OgImageValue = Object.freeze({
  id: IMAGE_PLACEHOLDER_FULL_ID,
});

/**
 * Single source of truth for OG image precedence.
 *
 * Both the Designer canvas (client) and SSR call this function with
 * environment-specific token replacement only.
 *
 * Precedence:
 * 1. pageOgImageAssetId → {id}.
 *    Asset-bound OG images stay as `{id}` in the shared page-binding/render path.
 *    Published absolute URLs for meta tags are resolved from assets in createRootNode.
 * 2. pageOgImage → replaceTokens(value) → {url} if truthy, else placeholder.
 *    replaceTokens handles both token resolution and static URL passthrough.
 * 3. Placeholder.
 *
 * The default replaceTokens callback passes through non-token strings unchanged
 * and returns null for unresolvable tokens, so callers without a CMS context
 * (e.g. SSR static pages) still resolve static OG image URLs correctly.
 */
export function resolveOgImageSource(
  pageOgImageAssetId: string | null | undefined,
  pageOgImage: string | null | undefined,
  replaceTokens: (value: string) => string | null = (value) =>
    parseTokenJson(value) ? null : value
): OgImageValue {
  // Step 1: asset ID
  if (pageOgImageAssetId) {
    return createOgImageFromAssetId(pageOgImageAssetId);
  }

  // Step 2: ogImage (token or static URL via replaceTokens)
  if (pageOgImage) {
    const imageUrl = replaceTokens(pageOgImage);
    if (imageUrl) {
      return createOgImageFromUrl(imageUrl);
    }
  }

  // Step 3: placeholder
  return PLACEHOLDER_OG_IMAGE;
}

/**
 * Helper for maintaining resolveOgImageSource return value referential equality.
 */
const createOgImageFromUrl = singleMemo((url: string): OgImageValue => ({url}));

/**
 * Helper for maintaining resolveOgImageSource return value referential equality.
 */
const createOgImageFromAssetId = singleMemo(
  (id: string): OgImageValue => ({id})
);
